import { AgGridReact, AgGridReactProps } from "ag-grid-react";
import { forwardRef, ForwardedRef, ReactElement, useImperativeHandle, useRef, useEffect } from 'react';
import { ColumnState, GridReadyEvent, RowHeightParams } from 'ag-grid-community';
import { createSaveColumnState, getRowStyle, onGridReady } from 'src/utils/grid/gridStyleUtils';
import { defaultAggridRowHeight, HandledProps } from 'src/components/common/common';
import { useGridCellSelection } from 'src/utils/grid/GridHandlerContent';

// Grid_Select에 필요한 props 정의. AgGridReactProps에서 공통 처리할 props는 제외합니다.
interface GridTouchProps<TData> extends Omit<AgGridReactProps<TData>, HandledProps> {
    setHoveredRowIndex: (index: number | null) => void;
    columnStateKey: string;
    setColumnState: (state: ColumnState[]) => void;
    onGridReady?: (params: GridReadyEvent<TData>) => void; // 부모에서 추가적인 onGridReady 로직을 실행할 경우를 대비

	rowHeight?: number;
}

// Grid_Select 컴포넌트 정의
function GridTouch<TData extends {} = any>(
    props: GridTouchProps<TData>,
    ref: ForwardedRef<AgGridReact<TData>>
): ReactElement {
    const {
        setHoveredRowIndex,
        columnStateKey,
        setColumnState,
        onGridReady: onGridReadyProp, //onGridReady
		rowHeight = defaultAggridRowHeight,
        ...restProps
    } = props;

    // 내부에서 사용할 ref. 부모에게 받은 ref와 동기화됩니다.
    const gridRef = useRef<AgGridReact<TData>>(null);
    const { 
        anchorCellRef,
        selectedCellRef,
        handleCellClick_targeting,
        handleCellKeyDown,
        handleCellMouseDown,
        handleCellMouseOver,
        clearRange,
        processedColDefs,
    } = useGridCellSelection({ 
        columnDefs: props.columnDefs,
	});

    useImperativeHandle(
        ref,
        () => {
            const inst = gridRef.current;
            if (!inst) return null as unknown as AgGridReact<TData>;
            return Object.assign(inst, {
                clearRange: () => {
                    const api = gridRef.current?.api;
                    if (api) clearRange(api);
                },
            }) as AgGridReact<TData>;
        },
        [clearRange]
    );

    const saveColumnState = createSaveColumnState( gridRef, columnStateKey, setColumnState );

    // 그리드가 준비되었을 때 실행될 공통 로직
    const handleGridReady = (params: GridReadyEvent<TData>) => {
        // 공통 onGridReady 로직 실행
        onGridReady(gridRef, columnStateKey, setColumnState);
        // 부모로부터 전달된 onGridReady가 있다면 실행
        if (onGridReadyProp) {
            onGridReadyProp(params);
        }
    };

    // [추가] 행 높이 계산 함수
    const getRowHeight = (params: RowHeightParams) => {
        // params.node.rowPinned는 'top', 'bottom', 또는 undefined(일반 행)입니다.
        // 모든 행(하단 포함)에 동일한 높이를 적용하려면 그냥 rowHeight 반환
        return rowHeight;
    };

    useEffect(() => {
        if (gridRef.current?.api) {
            gridRef.current.api.resetRowHeights();
        }
    }, [rowHeight]);

    return (
        <AgGridReact<TData>
            ref={gridRef}
			overlayNoRowsTemplate={'<span style="padding: 10px;">조회된 데이터가 없습니다.</span>'}
            {...restProps}

            columnDefs={processedColDefs}
            onCellClicked={handleCellClick_targeting}
            onCellMouseDown={handleCellMouseDown}
            onCellMouseOver={(params) => {
                if (params.node.rowPinned !== 'bottom') setHoveredRowIndex(params.node.rowIndex);
                handleCellMouseOver(params);
            }}
            onCellKeyDown={handleCellKeyDown}

            suppressScrollOnNewData={true}
            suppressColumnVirtualisation={true}
            suppressColumnMoveAnimation={true}
            getRowStyle={getRowStyle}
            onCellMouseOut={() => { setHoveredRowIndex(null); }}

            onGridReady={handleGridReady}
            onColumnMoved={saveColumnState}
            onColumnPinned={saveColumnState}
            onColumnVisible={saveColumnState}
            onColumnResized={saveColumnState}
            onSortChanged={saveColumnState}

			headerHeight={rowHeight}
			getRowHeight={getRowHeight}
        />
    );
}

// Grid_Select 컴포넌트를 내보내기
const Grid_Touch = forwardRef(GridTouch) as <TData extends {} = any>(
    props: GridTouchProps<TData> & { ref?: ForwardedRef<AgGridReact<TData>> }
) => ReactElement;


(Grid_Touch as React.FunctionComponent).displayName = "Grid_Touch";

export default Grid_Touch; 